<template>
  <div class="flex flex-col h-full overflow-hidden bg-white">
    <BaseTable
      v-if="relatedDocumentTypes"
      :headers="documentTypesHeaders"
      :list="relatedDocumentTypes"
      table-id="related-document-types"
      :is-add-new-hidden="true"
      :is-pagination-hidden="true"
      :is-search-hidden="true"
      :hide-grouping="true"
      :is-saving-layout-hidden="true"
      @refresh-data="refreshData">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="mode === 'edit'"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as DocumentType)" />
        </div>
      </template>
    </BaseTable>

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';

import type { DocumentType } from '@/types/DocumentTypes';
import type { TableFilterData } from '@/types/Base';
import { useI18n } from 'vue-i18n';

const props = withDefaults(
  defineProps<{
    childDocumentTypes?: DocumentType[];
    mode: 'view' | 'edit';
    isHorizontalLayout: boolean;
  }>(),
  {
    childDocumentTypes: () => [],
  }
);

const emit = defineEmits<{
  'delete-document-type': [documentType: DocumentType];
  'reload-data': [];
}>();

const { t: $t } = useI18n();
const modalDeleteRow = ref(false);
const deleteRowItem = ref<DocumentType>();

const documentTypesHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'division.name', text: $t('document-type.division'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'status', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

// Use computed to ensure reactivity when prop changes
const relatedDocumentTypes = computed(() => {
  return props.childDocumentTypes ? [...props.childDocumentTypes] : [];
});

async function refreshData(_data: TableFilterData) {
  emit('reload-data');
}

function onDeleteRow(item: DocumentType) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;

  emit('delete-document-type', deleteRowItem.value);
}
</script>
